import { EntityId, createSelector } from '@reduxjs/toolkit';
import { DialogMemberViewModel } from '@common/model/dialog/dialogMemberViewModel';
import { MemberUser } from '@youhelp-ru/core';
import { getDialogMemberForUser, getInitialParticipant } from '../../lib';
import { DialogReducerStateShape, DialogState } from './dialog';
import { dialogsAdapter } from './dialogsAdapter';
import { membersAdapter } from './membersAdapter';
import { messagesAdapter } from './messagesAdapter';

const { selectById: selectDialogById, selectIds: selectDialogIds } = dialogsAdapter.getSelectors();
const { selectById: selectMemberById, selectAll: selectAllMembers } = membersAdapter.getSelectors();
const { selectEntities: selectMessageEntities } = messagesAdapter.getSelectors();

export type UserToDialogParams = { user: MemberUser; dialogId: EntityId };
const userToDialogParamsFn = (_: unknown, params: UserToDialogParams) => params;
const entityIdFn = (_: unknown, id: EntityId) => id;
const dialogStateSelector = (state: DialogReducerStateShape) => state.dialog;

const selectDialogMembers = (state: DialogState, dialogId: EntityId) => {
  const dialog = selectDialogById(state.dialogs, Number(dialogId));
  const members: DialogMemberViewModel[] = [];
  if (!dialog) {
    return members;
  }
  let member: DialogMemberViewModel | undefined;
  for (const memberId of dialog.members) {
    member = selectMemberById(state.members, Number(memberId));
    if (!member) {
      continue;
    }
    members.push(member);
  }
  return members;
};

const selectInitialParticipant = (state: DialogState, params: UserToDialogParams) => {
  const members = selectDialogMembers(state, params.dialogId);
  return getInitialParticipant(members, params.user);
};

const selectCurrentMember = (state: DialogState, params: UserToDialogParams) => {
  const members = selectDialogMembers(state, params.dialogId);
  return getDialogMemberForUser(members, params.user);
};

export const dialogMembersSelector = createSelector([dialogStateSelector, entityIdFn], (state, dialogId) =>
  selectDialogMembers(state, dialogId),
);

export const initialParticipantMemberSelector = createSelector([dialogStateSelector, userToDialogParamsFn], (state, params) =>
  selectInitialParticipant(state, params),
);

export const currentDialogMemberSelector = createSelector([dialogStateSelector, userToDialogParamsFn], (state, params) =>
  selectCurrentMember(state, params),
);

export const memberByIdSelector = createSelector([dialogStateSelector, entityIdFn], (state, memberId) =>
  selectMemberById(state.members, Number(memberId)),
);

export const dialogsIdsSelector = createSelector((state: DialogReducerStateShape) => state.dialog.dialogs, selectDialogIds);

export const dialogByIdSelector = createSelector([dialogStateSelector, entityIdFn], (state, dialogId) =>
  selectDialogById(state.dialogs, Number(dialogId)),
);

export const dialogListCurrentPageSelector = (state: DialogReducerStateShape) => state.dialog.dialogsPage;

export const dialogListTotalPagesSelector = createSelector([dialogStateSelector], (state) =>
  Math.ceil(state.totalDialogs / state.dialogsPerPage),
);

export const dialogListPaginationSelector = createSelector([dialogStateSelector], (state) => state.dialogsPerPage);

export const dialogLastMessageSelector = createSelector([dialogStateSelector, entityIdFn], (state, dialogId) => {
  const dialog = selectDialogById(state.dialogs, Number(dialogId));
  return dialog?.lastMessage;
});

export const messagesDictSelector = createSelector([dialogStateSelector], (state) => selectMessageEntities(state.current.messages));
